<template>
	<ViewLayout>
		<template #header-title>
			Progressive Profiling
		</template>
		<template #content>
			<FilterBar
				:search-query.sync="progressiveProfilingSearchQuery"
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				search-query-label="Profile Name"
				@persistSearchQuery="changeProgressiveProfilingSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
			>
				<template #action>
					<PrimaryActionButton
						v-if="userCanCreateAndUpdate"
						@click="createOnClick"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Create
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<SectionCard>
				<template #title>
					Progressive Profiling
					<v-tooltip
						slot="append"
						right
					>
						<template #activator="{ on }">
							<v-icon
								light
								class="ml-2"
								v-on="on"
							>
								mdi-information
							</v-icon>
						</template>
						<span>
							This module allows the configuration of a chain of Embedded Consent Collections to obtain first party data on your data subjects.
						</span>
					</v-tooltip>
				</template>
				<template #body>
					<DataTable
						:headers="tableHeaders"
						:items="filteredProgressiveProfiles"
						sort-by="id"
						sort-desc
						@click:row="editOnClick"
					>
						<template #item.action="{ item }">
							<v-menu offset-y>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="userCanCreateAndUpdate && userHasBrand(item.brandId)"
										@click="progressiveProfileToEdit = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Edit
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-else
										@click="progressiveProfileToEdit = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												View
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanDelete && userHasBrand(item.brandId)"
										@click.stop.prevent="progressiveProfileToRemove = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Delete
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
					<ConfirmDeleteModal
						v-if="progressiveProfileToRemove"
						:entity-name="progressiveProfileToRemove.profileName"
						entity-type="Progressive Profile"
						@close="progressiveProfileToRemove = null"
						@delete="deleteProgressiveProfile"
					/>
				</template>
			</SectionCard>
		</template>
		<template #footer>
			<PageActionRow>
				<template #actions>
					<SecondaryActionButton @click="backToConsentCollection">
						Back to Consent Collection
					</SecondaryActionButton>
				</template>
			</PageActionRow>
		</template>
	</ViewLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'

import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { MANAGE_PROGRESSIVE_PROFILE, CONSENT_COLLECTION } from '../../../../router/route-names.js'
import { getProgressiveProfiles, deleteProgressiveProfile } from '../../../../../../shared/utils/api/progressive-profiling.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId } from '../../../../../../shared/state/brands.js'
import { changeProgressiveProfilingSearchQuery, progressiveProfilingSearchQuery } from '../../../../../../shared/state/progressive-profiling.js'
import { CAN_CREATE_UPDATE_PROGRESSIVE_PROFILING, CAN_DELETE_PROGRESSIVE_PROFILING } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'
export default {
	components: {
		PrimaryActionButton,
		FilterBar,
		DataTable,
		ConfirmDeleteModal,
		SectionCard,
		ViewLayout,
		PageActionRow,
		SecondaryActionButton
	},
	setup () {
		return {
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeProgressiveProfilingSearchQuery,
			progressiveProfilingSearchQuery
		}
	},
	data () {
		return {
			progressiveProfileToEdit: null,
			selectedBrandId: null,
			progressiveProfileToRemove: null,
			progressiveProfiles: [],
			tableHeaders: [
				{
					value: 'id',
					text: 'ID',
					width: '10%'
				},
				{
					value: 'brandName',
					text: useLabels('Brand'),
					width: '25%'
				},
				{
					value: 'profileName',
					text: 'Profile Name',
					width: '40%'
				},
				{
					value: 'createdDate',
					text: 'Created Date',
					width: '25%'
				},
				{
					value: 'action',
					text: 'Action',
					sortable: false,
					width: '80px',
					align: 'center'
				}
			],
			showAddModal: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission', 'userHasUniversalBrand', 'userBrands']),
		...mapGetters('lookupData', ['progressiveProfileLookupData']),
		filteredProgressiveProfiles () {
			return this.progressiveProfiles.filter(({ profileName, brandId }) => {
				let check = true
				if (progressiveProfilingSearchQuery.value) check = profileName.toLowerCase().includes(progressiveProfilingSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				return check
			})
		},
		userCanCreateAndUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_PROGRESSIVE_PROFILING)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_PROGRESSIVE_PROFILING)
		}
	},
	created () {
		this.loadProgressiveProfiling()
	},
	methods: {
		...mapActions('lookupData', ['storeProgressiveProfilingLookupData']),
		createOnClick () {
			this.$router.push({
				name: MANAGE_PROGRESSIVE_PROFILE,
				params: {
					userCanCreateAndUpdate: this.userCanCreateAndUpdate
				}
			})
		},
		editOnClick (progressiveProfileToEdit) {
			this.$router.push({
				name: MANAGE_PROGRESSIVE_PROFILE,
				params: {
					progressiveProfileToEdit: progressiveProfileToEdit,
					userCanCreateAndUpdate: this.userCanCreateAndUpdate && this.userHasUniversalBrand || this.userHasBrand(progressiveProfileToEdit.brandId)
				}
			})
		},
		async deleteProgressiveProfile () {
			await deleteProgressiveProfile(this.progressiveProfileToRemove.id)
			showSnackbar('Progressive Profile deleted successfully.')
			this.progressiveProfileToRemove = null
			this.loadProgressiveProfiling()
		},
		async loadProgressiveProfiling () {
			const { profiles, lookupData } = await getProgressiveProfiles()
			if (!this.progressiveProfileLookupData) {
				this.storeProgressiveProfilingLookupData(lookupData)
			}
			this.progressiveProfiles = profiles
		},
		userHasBrand (brandId) {
			return this.userBrands.includes(brandId)
		},
		backToConsentCollection () {
			this.$router.push({
				name: CONSENT_COLLECTION
			})
		}
	}
}
</script>
